import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HierarchyFilterComponent } from '../../../components/FilterBar/HierarchyFilter';
import { MetricBox } from '../../../components/MetricBox';
import { PerformanceTable } from '../../../components/tables/PerformanceTable';
import { RevenueChart } from '../../../components/charts/RevenueChart';
import { UnitsChart } from '../../../components/charts/UnitsChart';
import { FunnelChart } from '../../../components/charts/FunnelChart';
import { HierarchyFilter, SalesPerformanceData, StockFilter } from '../../../lib/types';
import { subDays } from 'date-fns';

export function SalesPerformance() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [filter, setFilter] = useState<HierarchyFilter>({
    dateFrom: subDays(new Date(), 30).toISOString(),
    dateTo: new Date().toISOString(),
    granularity: 'day',
    compareToPrevious: false,
    brandScope: 'sku_or_campaign',
  });

  const [data, setData] = useState<SalesPerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [salesFilter, setSalesFilter] = useState<StockFilter>({
    operatorIds: [],
    machineIds: [],
    productIds: []
  });
  const [revenueGranularity, setRevenueGranularity] = useState<'day' | 'week' | 'month' | 'year'>('day');
  const [unitsGranularity, setUnitsGranularity] = useState<'day' | 'week' | 'month' | 'year'>('day');
  const [topMachinesView, setTopMachinesView] = useState<'revenue' | 'units'>('revenue');
  const [topProductsView, setTopProductsView] = useState<'revenue' | 'units'>('revenue');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/auth/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        setUser(data.user);
      });
    }
  }, []);

  useEffect(() => {
    if (user?.tenantId) {
      // Debounce filter changes to prevent rapid API calls
      const timeoutId = setTimeout(() => {
        fetchSalesData();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [user, filter, salesFilter, revenueGranularity, unitsGranularity]);

  const fetchSalesData = async () => {
    try {
      setLoading(true);
      
      // Fetch data for both granularities if they differ
      const granularities = new Set([revenueGranularity, unitsGranularity]);
      const dataPromises = Array.from(granularities).map(async (granularity) => {
        const params = new URLSearchParams({
          dateFrom: filter.dateFrom,
          dateTo: filter.dateTo,
          granularity: granularity,
          compareToPrevious: String(filter.compareToPrevious || false),
          brandScope: filter.brandScope || 'sku_or_campaign',
        });

        // Add multi-select filter parameters
        if (salesFilter.operatorIds.length > 0) {
          salesFilter.operatorIds.forEach(id => params.append('operatorIds', id));
        }
        if (salesFilter.machineIds.length > 0) {
          salesFilter.machineIds.forEach(id => params.append('machineIds', id));
        }
        if (salesFilter.productIds.length > 0) {
          salesFilter.productIds.forEach(id => params.append('productIds', id));
        }

        const token = localStorage.getItem('token');
        const response = await fetch(`/api/brand/${user.tenantId}/dashboard/sales?${params}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch sales data');
        }

        return { granularity, data: await response.json() };
      });

      const results = await Promise.all(dataPromises);
      
      // Combine the data with the appropriate granularity
      const revenueData = results.find(r => r.granularity === revenueGranularity)?.data;
      const unitsData = results.find(r => r.granularity === unitsGranularity)?.data;
      
      // Use the first result as base and merge the time series
      const combinedData = {
        ...revenueData,
        timeSeries: {
          revenue: revenueData.timeSeries.revenue,
          units: unitsData.timeSeries.units,
          salesByTime: revenueData.timeSeries.salesByTime
        }
      };
      
      setData(combinedData);
    } catch (error) {
      console.error('Error fetching sales data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    console.log('Export CSV');
  };

  const handleExportPNG = () => {
    console.log('Export PNG');
  };

  const handleExportPagePDF = () => {
    console.log('Export Page PDF');
  };

  const handleSeeAllTopMachines = () => {
    navigate(`/brand/${brandId}/dashboard/sales/top-machines/all`);
  };

  const handleSeeAllTopProducts = () => {
    navigate(`/brand/${brandId}/dashboard/sales/top-products/all`);
  };

  const handleSeeAllBottomMachines = () => {
    navigate(`/brand/${brandId}/dashboard/sales/bottom-machines/all`);
  };

  const handleSeeAllBottomProducts = () => {
    navigate(`/brand/${brandId}/dashboard/sales/bottom-products/all`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-600">Failed to load data</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Filter Section with proper separation */}
      <div className="bg-white shadow-sm">
        <HierarchyFilterComponent
          filter={filter}
          onChange={setFilter}
          onExportCSV={handleExportCSV}
          onExportPNG={handleExportPNG}
          onExportPagePDF={handleExportPagePDF}
          showBrandScope={true}
        />
      </div>

      {/* Main Content with proper spacing */}
      <div className="max-w-7xl mx-auto p-6 pt-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Sales Performance</h1>
          <p className="text-gray-600">Analyze revenue, units, and conversion performance</p>
        </div>

        {/* Sales Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <MetricBox title="Total Revenue" value={data.metrics.totalRevenue} format="currency" decimals={2} />
          <MetricBox title="Total Units" value={data.metrics.totalUnits} format="number" />
          <MetricBox title="Average Transaction Value" value={data.metrics.averageTransactionValue} format="currency" decimals={2} />
          <MetricBox title="Average Rate of Sale" value={data.metrics.averageRateOfSale} format="number" decimals={2} subtitle="vends/day" />
        </div>

        {/* Charts with integrated filters */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <RevenueChart 
            data={data.timeSeries.revenue} 
            salesFilter={salesFilter}
            onSalesFilterChange={setSalesFilter}
            operators={data?.operators || []}
            machines={data?.machines || []}
            products={data?.products || []}
            granularity={revenueGranularity}
            onGranularityChange={setRevenueGranularity}
          />
          <UnitsChart 
            data={data.timeSeries.units}
            salesFilter={salesFilter}
            onSalesFilterChange={setSalesFilter}
            operators={data?.operators || []}
            machines={data?.machines || []}
            products={data?.products || []}
            granularity={unitsGranularity}
            onGranularityChange={setUnitsGranularity}
          />
        </div>

        {/* Funnel Chart */}
        <div className="mb-8">
          <FunnelChart data={data.funnel} />
        </div>

        {/* Performance Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <PerformanceTable
            title="Top Performing Machines"
            data={data.topMachines}
            viewType={topMachinesView}
            onToggleView={() => setTopMachinesView(topMachinesView === 'revenue' ? 'units' : 'revenue')}
            onSeeAll={handleSeeAllTopMachines}
          />
          <PerformanceTable
            title="Top Performing Products"
            data={data.topProducts}
            viewType={topProductsView}
            onToggleView={() => setTopProductsView(topProductsView === 'revenue' ? 'units' : 'revenue')}
            onSeeAll={handleSeeAllTopProducts}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <PerformanceTable
            title="Worst Performing Machines"
            data={data.bottomMachines}
            viewType={topMachinesView}
            onToggleView={() => setTopMachinesView(topMachinesView === 'revenue' ? 'units' : 'revenue')}
            onSeeAll={handleSeeAllBottomMachines}
          />
          <PerformanceTable
            title="Worst Performing Products"
            data={data.bottomProducts}
            viewType={topProductsView}
            onToggleView={() => setTopProductsView(topProductsView === 'revenue' ? 'units' : 'revenue')}
            onSeeAll={handleSeeAllBottomProducts}
          />
        </div>
      </div>
    </div>
  );
}
